import { KlarnaPaymentMessageUI } from "./KlarnaPaymentMessageUI";
import { StandardProps } from "../componentMap";
import { useVariant } from "@/hooks/useVariant/useVariant";
import { ensureIsActive } from "@/utils/product/ensureIsActive";
import { CreditIllustration } from "@/types/cobeTypes";
import { getColourGroupForVariant } from "@/utils/product/getColourGroupForVariant.ts";
import { getVariantsForGroup } from "@/components/Colours/helpers.ts";
import { Variant } from "@/types/types.ts";

export function KlarnaPaymentMessage({
  product,
  creditOffersV2,
  runtimeConfig,
  flags,
}: Pick<
  StandardProps,
  "product" | "creditOffersV2" | "runtimeConfig" | "flags"
>) {
  let messageType: "GENERIC" | "INSTALMENT" | null = null;

  const { isSelected, variant } = useVariant(product);

  const showKlarnaMessage = !!flags.showKlarnaMessage.value;

  const activeProduct = ensureIsActive(product);

  if (!activeProduct) {
    return null;
  }

  if (isSelected) {
    const variantEligible = creditOffersV2?.some(
      ({ skuId, creditIllustrations }) => {
        if (skuId === variant?.aliases.skuId) {
          return creditIllustrations.some(
            (illustration: CreditIllustration) =>
              illustration.creditProvider?.toLowerCase() === "klarna",
          );
        }
        return false;
      },
    );
    messageType = variantEligible ? "INSTALMENT" : null;
  } else {
    const variantGroup = getColourGroupForVariant(
      product.variantGroups,
      variant.id,
    );

    const priceRange =
      variantGroup?.variantPriceRange || activeProduct?.variantPriceRange;

    const hasPriceRange =
      priceRange?.display?.min !== undefined &&
      priceRange?.display?.max !== undefined &&
      priceRange?.display?.min !== priceRange?.display?.max;

    const allVariants = variantGroup
      ? getVariantsForGroup(variantGroup, product)
      : product.variants;

    const variantGroupEligibleForKlarna = allVariants.some((variant: Variant) =>
      creditOffersV2.some(
        ({ skuId, creditIllustrations }) =>
          skuId === variant.aliases.skuId &&
          creditIllustrations.some(
            (illustration: CreditIllustration) =>
              illustration.creditProvider?.toLowerCase() === "klarna",
          ),
      ),
    );

    if (variantGroupEligibleForKlarna) {
      messageType = hasPriceRange ? "GENERIC" : "INSTALMENT";
    } else {
      messageType = null;
    }
  }

  const amount = String(
    messageType === "INSTALMENT"
      ? Math.round(Number(variant.price?.value) * 100)
      : 10,
  );

  return (
    showKlarnaMessage &&
    messageType && (
      <KlarnaPaymentMessageUI
        runtimeConfig={runtimeConfig}
        currentPrice={amount}
        variantsEligibleForKlarna={messageType}
      />
    )
  );
}
